import type { FC, MouseEvent } from 'react';

import { type Editor, useEditorState } from '@tiptap/react';

import { tr } from '../constants';
import { BoldIcon, CodeIcon, ItalicIcon, StrikeIcon, UnderlineIcon } from './icons';
import { ToolbarButton } from './ToolbarButton';

interface FormatButtonsProps {
  editor: Editor;
  translations?: Record<string, string>;
}

const FORMAT_MARKS = [
  { name: 'bold', labelKey: 'community.composer.format_bold', Icon: BoldIcon },
  { name: 'italic', labelKey: 'community.composer.format_italic', Icon: ItalicIcon },
  { name: 'underline', labelKey: 'community.composer.format_underline', Icon: UnderlineIcon },
  { name: 'strike', labelKey: 'community.composer.format_strike', Icon: StrikeIcon },
  { name: 'code', labelKey: 'community.composer.format_code', Icon: CodeIcon },
] as const;

const preserveSelection = (event: MouseEvent<HTMLButtonElement>) => {
  event.preventDefault();
};

export const FormatButtons: FC<FormatButtonsProps> = ({ editor, translations }) => {
  const editorState = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
    }),
  });

  return (
    <div
      role="toolbar"
      aria-label={tr(translations, 'community.composer.format_text')}
      className="flex items-center gap-2"
    >
      {FORMAT_MARKS.map(({ name, labelKey, Icon }) => (
        <ToolbarButton
          key={name}
          label={tr(translations, labelKey)}
          icon={<Icon />}
          onClick={() => editor.chain().focus().toggleMark(name).run()}
          active={editorState[name]}
          buttonProps={{ onMouseDown: preserveSelection }}
        />
      ))}
    </div>
  );
};
